import AnimationController from '../../assets/js/controllers/animation';
import CursorController from '../../assets/js/controllers/cursor';
import ResizeController from '../../assets/js/controllers/resize';

import { getRootOffsetBounds } from '../../assets/js/helpers';

export default class ActivityDetector {

	static selector = '[data-activity-detector]';

	// class variables

	config = {
		inactivityDelay: 1000,	// msec
		minDistance: 2			// pixels
	};

	controllers = {
		animation: null,
		resize: null
	};

	coords = {
		container: null
	};

	el = {};

	flags = {
		isActive: false,
		isActivePending: false,
		isInactivePending: false
	};

	states = {
		lastActive: {
			cursorX: 0,
			cursorY: 0
		}
	};

	timeouts = {
		inactive: null
	};

	// constructor

	constructor(container) {
		this.el = {
			container
		};

		this.initialize();
	}

	// init

	initialize() {
		this.#addEventListeners();

		this.measureContainer();
		this.inactive();
	}

	// event listeners

	#addEventListeners = () => {
		document.addEventListener('keydown', this.#onKeyDown);

		this.controllers.animation = AnimationController.set({
			update: this.#onAnimationUpdate,
			render: this.#onAnimationRender
		});

		this.controllers.resize = ResizeController.set({
			update: this.#onResizeUpdate,
			render: this.#onResizeRender
		});
	}

	#removeEventListeners = () => {
		document.removeEventListener('keydown', this.#onKeyDown);
		AnimationController.clear(this.controllers.animation);
		ResizeController.clear(this.controllers.resize);
	}

	// listener methods

	#onKeyDown = () => {
		this.active();
	}

	// controller methods

	#onAnimationUpdate = () => this.measureActivity();

	#onAnimationRender = () => {
		this.adjustActiveState();
	}

	#onResizeUpdate = () => {
		this.measureContainer();
		return this.measureActivity();
	}

	#onResizeRender = () => {
		this.adjustActiveState();
	}

	// measure container bounds

	measureContainer() {
		this.coords.container = getRootOffsetBounds(this.el.container);
	}

	// check activity

	measureActivity() {
		if (this.cursorDistance >= this.config.minDistance && this.isMouseOver) {
			this.flags.isActivePending = true;
			this.states.lastActive.cursorX = CursorController.cursorX;
			this.states.lastActive.cursorY = CursorController.cursorY;

			if (!this.flags.isActive) {
				return true;
			}

			if (this.flags.isInactivePending) {
				this.killTimeoutInactivity();
			}
		} else if (this.flags.isActive) {
			return true;
		}
	}

	adjustActiveState() {
		if (this.flags.isActivePending) {
			if (!this.flags.isActive) {
				this.active();
			}
		} else if (this.flags.isActive) {
			this.inactive();
		}

		this.flags.isActivePending = false;
	}

	// mark active

	active() {
		this.flags.isActive = true;
		this.killTimeoutInactivity();
		this.el.container.classList.remove('is-user-inactive');
		this.el.container.classList.add('is-user-active');
	}

	inactive() {
		if (!this.timeouts.inactive) {
			this.flags.isInactivePending = true;
			this.timeouts.inactive = setTimeout(() => {
				this.killTimeoutInactivity();
				this.flags.isActive = false;
				this.el.container.classList.remove('is-user-active');
				this.el.container.classList.add('is-user-inactive');
			}, this.config.inactivityDelay);
		}
	}

	killTimeoutInactivity() {
		this.flags.isInactivePending = false;
		if (this.timeouts.inactive) {
			clearTimeout(this.timeouts.inactive);
			this.timeouts.inactive = null;
		}
	}

	// getters

	get cursorDistance() {
		return Math.floor(((this.states.lastActive.cursorX - CursorController.cursorX) ** 2) + ((this.states.lastActive.cursorY - CursorController.cursorY) ** 2));
	}

	get isMouseOver() {
		if (CursorController.cursorX >= this.coords.container.left && CursorController.cursorX <= this.coords.container.right
			&& CursorController.cursorY >= this.coords.container.top && CursorController.cursorY <= this.coords.container.bottom) {
			return true;
		}

		return false;
	}

	// destroy

	destroy() {
		this.killTimeoutInactivity();
		this.#removeEventListeners();
	}

}
